/*
 * Science timeline — scroll-driven step reveal.
 *
 * A step becomes active when it crosses a trigger line in the viewport; the
 * rail fills to match. Progress is monotonic per scroll position, so scrolling
 * back up steps the timeline back down rather than leaving everything open.
 *
 * Degradation is deliberate: the section renders fully expanded and readable,
 * and collapsing is only switched on after JS proves it can reopen a step.
 * A timeline that collapses but cannot reopen would hide the copy entirely.
 */
(function () {
  'use strict';

  class ScienceTimeline extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('.st__step'));
      this.fill = this.querySelector('.st__rail-fill');
      if (this.steps.length === 0) return;

      this.accumulate = this.dataset.reveal === 'accumulate';
      this.trigger = parseFloat(this.dataset.trigger || '0.6');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.bodies = this.steps.map((s) => s.querySelector('.st__step-body'));

      if (!this.enableCollapse()) return;

      this.track = this.closest('.st');
      this.pin = this.track ? this.track.querySelector('.st__pin') : null;
      // Only now is the track allowed to grow: the CSS is gated on this class
      // so a page whose script never ran is never left with several screenfuls
      // of scrolling and nothing happening.
      if (this.track && this.pin && this.track.classList.contains('st--pinned')) {
        this.track.classList.add('st--pin-ready');
      }

      this.measure();

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.ticking = false;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.reduced.addEventListener('change', () => {
        this.measure();
        this.update();
      });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    /*
     * What each step measures with its body shut, and what each body measures
     * when open. Both are needed to place the rail segment: the bodies are
     * mid-transition whenever update() runs, so their rects report the height
     * they are passing through rather than the one they are heading for, and a
     * segment measured from those would chase the animation instead of moving
     * with it. Re-taken on resize, where the copy rewraps.
     */
    measure() {
      const list = this.querySelector('.st__steps');
      this.gap = list ? parseFloat(getComputedStyle(list).rowGap) || 0 : 0;

      this.baseHeights = this.steps.map((step, i) => {
        const body = this.bodies[i];
        const open = body ? body.getBoundingClientRect().height : 0;
        return step.getBoundingClientRect().height - open;
      });
      this.bodyHeights = this.bodies.map((b) => (b ? b.scrollHeight : 0));
    }

    onResize() {
      this.measure();
      this.onScroll();
    }

    /*
     * Collapse one body and confirm it can be measured open again. If the
     * height cannot be driven, leave every step expanded and bail out — a
     * visible timeline without animation beats an invisible one.
     */
    enableCollapse() {
      const probe = this.bodies.find((b) => b && b.scrollHeight > 0);
      if (!probe) return false;

      const natural = probe.scrollHeight;
      probe.style.height = '0px';
      probe.style.height = natural + 'px';
      const ok = probe.getBoundingClientRect().height > 0;
      probe.style.height = '';

      if (!ok) return false;

      this.classList.add('st--enhanced');
      this.bodies.forEach((b) => {
        if (b) b.style.height = '0px';
      });
      return true;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /*
     * Whether the section is currently held. Asking the stylesheet rather than
     * re-deriving it here keeps the breakpoint, the reduced-motion opt-out and
     * the setting in one place — CSS — and stays correct across a resize
     * without anything having to be recalculated.
     */
    get pinned() {
      return !!this.pin && getComputedStyle(this.pin).position === 'sticky';
    }

    /*
     * Which step the visitor has scrolled to inside the track. The track is one
     * screenful taller than the pin, plus the dwell per step; how far its top
     * has travelled past the top of the viewport, over that surplus, is the
     * progress through the steps.
     */
    pinnedIndex() {
      const box = this.track.getBoundingClientRect();
      const travel = box.height - this.pin.offsetHeight;
      if (travel <= 0) return 0;

      const progress = Math.min(Math.max(-box.top / travel, 0), 1);
      // floor() gives each step an equal share; the last one would only be hit
      // exactly at progress === 1, so it is clamped in.
      return Math.min(this.steps.length - 1, Math.floor(progress * this.steps.length));
    }

    /* Index of the last step whose top has crossed the trigger line. */
    currentIndex() {
      const line = window.innerHeight * this.trigger;
      let index = -1;
      this.steps.forEach((step, i) => {
        if (step.getBoundingClientRect().top <= line) index = i;
      });
      return index;
    }

    update() {
      // Reduced motion: reveal everything, no scroll dependence.
      if (this.reduced.matches) {
        this.steps.forEach((step, i) => {
          step.dataset.active = 'true';
          step.dataset.past = 'false';
          this.setBody(i, true);
        });
        this.markRail(0, this.steps.length - 1, () => true);
        return;
      }

      const current = this.pinned ? this.pinnedIndex() : this.currentIndex();
      const isOpen = (i) => (this.accumulate ? i <= current : i === current);

      this.steps.forEach((step, i) => {
        const active = isOpen(i);
        step.dataset.active = String(active);
        step.dataset.past = String(i < current);
        this.setBody(i, active);
      });

      this.markRail(this.accumulate ? 0 : current, current, isOpen);
    }

    /*
     * Puts the rail's solid segment alongside the open step(s) — it marks where
     * you are, it does not fill from the top. Measured off the Figma rails: with
     * the first step open the segment runs 0-96px, with the last 232-323px.
     *
     * Positions are added up from the measured heights rather than read back off
     * the DOM, so the segment moves to where the layout is going rather than to
     * where it currently is halfway through the body's transition.
     */
    markRail(from, to, isOpen) {
      this.dataset.atStart = String(from <= 0 && to >= 0);
      this.dataset.atEnd = String(to >= this.steps.length - 1);

      if (!this.fill) return;

      if (from < 0 || to < 0) {
        this.fill.style.setProperty('--st-thumb-height', '0px');
        return;
      }

      let y = 0;
      let top = 0;
      let bottom = 0;

      for (let i = 0; i < this.steps.length; i += 1) {
        const height = this.baseHeights[i] + (isOpen(i) ? this.bodyHeights[i] : 0);
        if (i === from) top = y;
        if (i === to) bottom = y + height;
        y += height + this.gap;
      }

      this.fill.style.setProperty('--st-thumb-top', top.toFixed(1) + 'px');
      this.fill.style.setProperty('--st-thumb-height', Math.max(0, bottom - top).toFixed(1) + 'px');
    }

    setBody(i, open) {
      const body = this.bodies[i];
      if (!body) return;
      // scrollHeight is read while collapsed, which still reports content height.
      body.style.height = open ? body.scrollHeight + 'px' : '0px';
    }
  }

  if (!customElements.get('science-timeline')) {
    customElements.define('science-timeline', ScienceTimeline);
  }
})();
